import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { AverageScoreTeamMode } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconAlert } from '@sorare/core/src/atoms/icons/system/iconAlert.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelXS } from '@sorare/core/src/atoms/typography';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PlayerGameStatsPrediction } from '#src/components/game/PlayerGameStatsPrediction/index.tsx';
import { SingleMatchupItem } from '#src/components/game/SingleMatchupItem/index.tsx';

import { CalendarTooltip } from './CalendarTooltip/index.tsx';
import type { AnyPlayerUpcomingGames_anyPlayer } from './__generated__/index.graphql.ts';

type Props = {
  anyPlayer: AnyPlayerUpcomingGames_anyPlayer;
  teamMode?: AverageScoreTeamMode | null;
};

const Root = styled(Horizontal)`
  background-color: var(--current-row-color);
  padding: 0 var(--unit);
  display: none;
  @media ${laptopAndAbove} {
    display: flex;
    background-color: var(--current-row-color);
    padding: 0 var(--unit);
  }
`;
const RelativeWrapper = styled(Vertical).attrs({ gap: 0.5 })`
  position: relative;
  isolation: isolate;
`;
const EmptyWrapper = styled(Horizontal).attrs({ center: true })`
  height: var(--triple-unit);
  max-width: 120px;
  width: 100%;
`;

const Matchup = styled(Horizontal).attrs({ gap: 0 })`
  height: var(--triple-unit);
  min-width: 120px;
  border-radius: var(--half-unit);
  background: var(--c-nd-50);
  overflow: hidden;
`;

const Absolute = styled(Horizontal).attrs({ gap: 0.5 })`
  position: absolute;
  top: calc(100% + var(--half-unit));
  left: 0;
  right: 0;
`;

const Divider = styled.div`
  height: 2px;
  width: 2px;
  background-color: var(--c-nd-150);
  border-radius: 50%;
  flex-shrink: 0;
`;

export const AnyPlayerUpcomingGames = ({ anyPlayer, teamMode }: Props) => {
  const nextGameStat = anyPlayer.anyFutureGameStats[0];
  const nextGame = nextGameStat?.anyGame;

  const playingStatusOdds = isType(nextGameStat, 'PlayerGameStats')
    ? nextGameStat.footballPlayingStatusOdds
    : null;

  const fixtureName = nextGame?.so5Fixture?.shortDisplayName;

  return (
    <Root gap={0.5} center>
      {nextGame ? (
        <RelativeWrapper>
          <Matchup>
            <SingleMatchupItem
              anyGame={nextGame}
              playerTeamSlug={nextGameStat?.anyTeam.slug}
            />
          </Matchup>
          <Absolute center>
            <LabelXS color="var(--c-nd-600)">
              {fixtureName || (
                <FormattedDate
                  value={nextGame.date}
                  day="numeric"
                  month="short"
                />
              )}
            </LabelXS>
            {!!playingStatusOdds && (
              <>
                <Divider />
                <LabelXS>
                  <PlayerGameStatsPrediction
                    variant="text"
                    playingStatusOdds={playingStatusOdds}
                    includesIcon={false}
                  />
                </LabelXS>
              </>
            )}
          </Absolute>
        </RelativeWrapper>
      ) : (
        <EmptyWrapper>
          <FontAwesomeIcon
            icon={iconAlert}
            color="var(--c-yellow-400)"
            width={10}
          />
          <LabelXS className="text-center">
            <FormattedMessage defaultMessage="No upcoming game" />
          </LabelXS>
        </EmptyWrapper>
      )}
      <CalendarTooltip
        anyPlayer={anyPlayer}
        hasAGame={!!nextGame}
        teamMode={teamMode}
      />
    </Root>
  );
};

AnyPlayerUpcomingGames.fragments = {
  anyPlayer: gql`
    fragment AnyPlayerUpcomingGames_anyPlayer on AnyPlayerInterface {
      slug
      anyFutureGameStats(first: 1, teamMode: $teamMode) {
        id
        anyTeam {
          slug
        }
        anyGame {
          id
          date
          so5Fixture {
            slug
            shortDisplayName
          }
          ...SingleMatchupItem_anyGame
        }
        ... on PlayerGameStats {
          id
          footballPlayingStatusOdds {
            reliability
            ...PlayerGameStatsPrediction_playingStatusOdds
          }
        }
      }
      ...CalendarTooltip_anyPlayer
    }
    ${SingleMatchupItem.fragments.anyGame}
    ${PlayerGameStatsPrediction.fragments.playingStatusOdds}
    ${CalendarTooltip.fragments.anyPlayer}
  ` as TypedDocumentNode<AnyPlayerUpcomingGames_anyPlayer>,
};
